import React, { useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import {
    CampaignName,
    ContentContainer,
    DatesContainer,
    DateTitle,
    DateValue,
    DateWrapper,
    ImageContainer,
    ActionsWrapper,
    TextAreaCopyComponent,
    Indicator,
    IndicatorsWrapper,
    ActiveLabel,
    LinkWrapper,
    BannerWrapper,
    CopiedMessage,
    ActionButtonAnimated,
    BannerLink,
    CopyComponentWrapper,
} from './LPListItem.style';
import { LandingPageState, PromotionLandingPageType } from 'src/AppState/LandingPageState/LandingPageState';
import moment from 'moment';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { observable, makeAutoObservable } from 'mobx';
import { UniverseType } from 'src_common/common/universe';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import { ListItem, TrashIconWrapper } from 'src/domains/layouts/shared/cms/list/List.style';
import { getUniverseModule } from 'src/domains/common/universes';
import { Common, useCommon } from 'src/AppState/Common';

// eslint-disable-next-line @typescript-eslint/naming-convention
interface LPPropsType {
    LP: PromotionLandingPageType;
}

enum ShowActionEnum {
    LOGIN = 'login',
    SIGNUP = 'signup',
    DIRECT = 'direct-link',
}

enum BannerEnum {
    HTML = 'html',
    STATIC = 'static',
}
class State {
    @observable public copySuccess = false;
    public constructor(
        private readonly common: Common,
        private readonly landingPageState: LandingPageState,
        public readonly textAreaRef: React.RefObject<HTMLTextAreaElement>,
        public readonly universe: UniverseType
    ) {
        makeAutoObservable(this);
    }

    public onChangePageToEdit = (LP: PromotionLandingPageType): void => {
        this.landingPageState.setPage('edit');
        this.landingPageState.setEditedLP(LP);
    };

    public onRemove = async (LP: PromotionLandingPageType): Promise<void> => {
        await this.landingPageState.onRemove(LP.id);
    };

    public changeActiveFlag = async (LP: PromotionLandingPageType): Promise<void> => {
        await this.landingPageState.setActiveFlag(LP);
    };

    public copyToClipboard = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        this.textAreaRef.current?.select();
        document.execCommand('copy');
        // @ts-expect-error
        e.target.focus();
        this.copySuccess = true;
        setTimeout(() => {
            this.copySuccess = false;
        }, 3200);
    };

    public showActionLabel = (value: string): string => {
        switch (value) {
            case ShowActionEnum.DIRECT:
                return 'Direct link';
            case ShowActionEnum.LOGIN:
                return 'Login';
            case ShowActionEnum.SIGNUP:
                return 'Sign up';
            default:
                return '';
        }
    };

    public showBannerLabel = (value: string): string => {
        switch (value) {
            case BannerEnum.HTML:
                return 'HTML5';
            case BannerEnum.STATIC:
                return 'Static image';
            default:
                return '';
        }
    };

    public getCopyLink = (LP: PromotionLandingPageType): string => {
        if (LP.is_homepage) {
            return this.getUniverseLink();
        } else {
            return `${this.getUniverseLink()}?promo=${LP.slug}`;
        }
    };

    public getUniverseLink = (): string => {
        const {
            urls: { website },
        } = getUniverseModule(this.common);
        return website;
    };
}

export const LPListItem = observer(({ LP }: LPPropsType) => {
    const common = useCommon();
    const { landingPageState, universe_theme: universe } = useAppStateContext();
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [state] = React.useState(() => new State(common, landingPageState, textAreaRef, universe));
    const startDate = moment(LP.date_from).format('DD-MM-YYYY HH:mm:ss');
    const endDate = moment(LP.date_to).format('DD-MM-YYYY HH:mm:ss');
    const isEdited = LP.slug === landingPageState.editedLP?.slug;

    return (
        <ListItem
            isEdited={isEdited}
            homepage={LP.is_homepage}
            boxHeight='200px'
            direction='row'
        >
            {LP.banner_type === BannerEnum.HTML ? (
                <BannerWrapper>
                    <LinkWrapper>
                        <BannerLink
                            target='_blank'
                            href={LP.img_html_url ?? ''}
                        >
                            Show HTML banner
                        </BannerLink>
                    </LinkWrapper>
                </BannerWrapper>
            ) : (
                <ImageContainer imgSrc={LP.img_url ?? ''} />
            )}
            <ContentContainer>
                <CampaignName data-test='lp-name'>{LP.title}</CampaignName>
                <DatesContainer>
                    <DateWrapper isStart>
                        <DateTitle>Scheduled start</DateTitle>
                        <DateValue>{startDate}</DateValue>
                    </DateWrapper>
                    <DateWrapper>
                        <DateTitle>Scheduled finish</DateTitle>
                        <DateValue>{endDate}</DateValue>
                    </DateWrapper>
                </DatesContainer>

                <IndicatorsWrapper>
                    {LP.is_homepage ? <Indicator homepage>Homepage LP</Indicator> : null}
                    <Indicator data-test='lp-action-type'>{state.showActionLabel(LP.action_template)}</Indicator>
                    <Indicator data-test='lp-banner-type'>{state.showBannerLabel(LP.banner_type)}</Indicator>
                </IndicatorsWrapper>
                <ActionsWrapper>
                    <Checkbox
                        labelSide='right'
                        onChange={() => state.changeActiveFlag(LP)}
                        isChecked={LP.is_active}
                        isDisabled={false}
                    >
                        <ActiveLabel>Active</ActiveLabel>
                    </Checkbox>
                    {state.copySuccess ? (
                        <CopiedMessage>Copied!</CopiedMessage>
                    ) : (
                        <ActionButtonAnimated
                            size='small'
                            onClick={state.copyToClipboard}
                            dataTest='copy-promo-url-button'
                        >
                            Copy Promo URL
                        </ActionButtonAnimated>
                    )}
                    <Button
                        version='secondary'
                        size='small'
                        onClick={() => state.onChangePageToEdit(LP)}
                        dataTest='edit-lp-button'
                    >
                        Edit
                    </Button>
                    <TrashIconWrapper onClick={() => state.onRemove(LP)} />
                </ActionsWrapper>

                <CopyComponentWrapper>
                    <TextAreaCopyComponent
                        ref={state.textAreaRef}
                        defaultValue={state.getCopyLink(LP)}
                    />
                </CopyComponentWrapper>
            </ContentContainer>
        </ListItem>
    );
});
